/**
 * <splide-carousel> — reusable Splide wrapper that drives a custom
 * carousel-nav (progress bar + arrows) instead of Splide's built-in UI.
 *
 * Options come from data-* attributes so it can be reused by any section:
 *   data-center            focus the active slide in the centre ("center mode")
 *   data-loop              infinite loop
 *   data-per-page          slides per page (default 1)
 *   data-per-page-mobile   slides per page <= 749px
 *   data-gap               gap between slides (CSS length, default '1.5rem')
 *   data-padding           side padding so neighbours peek (e.g. '14%')
 *   data-padding-mobile    side padding <= 749px
 *   data-autoplay          autoplay on/off
 *   data-interval          autoplay delay in ms
 *   data-speed             transition speed in ms
 *   data-pause-on-hover    pause autoplay on hover
 *
 * Nav hooks (rendered by the carousel-nav snippet) are discovered inside the
 * element: [data-carousel-prev], [data-carousel-next], [data-carousel-progress-fill].
 */
if (!customElements.get('splide-carousel')) {
  class SplideCarousel extends HTMLElement {
    connectedCallback() {
      if (typeof Splide === 'undefined') {
        window.addEventListener('load', () => this.tryInit(), { once: true });
      } else {
        this.tryInit();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    // Init now if laid out; otherwise wait. A carousel inside a hidden tab
    // panel has no layout (offsetParent === null) and Splide would mis-measure,
    // so we defer until ensureMounted() is called when the panel is shown.
    tryInit() {
      if (typeof Splide === 'undefined' || this.splide) return;
      if (this.offsetParent === null) {
        this.pending = true;
        return;
      }
      this.init();
    }

    // Called when a tab panel becomes visible: mount if pending, else refresh.
    ensureMounted() {
      if (this.splide) {
        this.splide.refresh();
        if (this.center) this.scheduleCoverflow();
      } else {
        this.tryInit();
      }
    }

    init() {
      const root = this.querySelector('.splide');
      if (!root || this.splide || typeof Splide === 'undefined') return;
      if (root.querySelectorAll('.splide__slide').length === 0) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.progressFill = this.querySelector('[data-carousel-progress-fill]');
      this.nav = this.querySelector('.carousel-nav');
      this.loop = this.dataset.loop === 'true';
      this.center = this.dataset.center === 'true';

      // Coverflow tuning.
      // minScale: size of the neighbour slides (centre is 1).
      // spread: resting horizontal offset of each neighbour from the centre,
      //   as a % of slide width. Small values stack neighbours BEHIND the centre
      //   so only their OUTER edge peeks (left slide -> left edge, right -> right edge).
      this.minScale = parseFloat(this.dataset.minScale) || 0.76;
      this.spread = parseFloat(this.dataset.spread);
      if (Number.isNaN(this.spread)) this.spread = 26;

      this.splide = new Splide(root, this.options());

      this.splide.on('mounted move moved updated resized', () => this.refreshNav());

      if (this.center) {
        this.applyCoverflow = this.applyCoverflow.bind(this);
        this.runFrame = this.runFrame.bind(this);
        // Drive the coverflow transforms in step with Splide's movement.
        this.splide.on('move drag scroll', () => this.startFrameLoop());
        this.splide.on('moved dragged scrolled', () => this.stopFrameLoop());
        // Initial/relayout passes are deferred a frame: on 'mounted' Splide has
        // not finished positioning the slides, so measuring immediately yields
        // d≈0 for everything and the slides stack at the centre. scheduleCoverflow
        // waits for layout to settle.
        this.splide.on('mounted updated resized', () => this.scheduleCoverflow());
      }

      this.splide.mount();
      this.bindArrows();

      // Safety net: recompute once the track is genuinely laid out, and again
      // after images load or the viewport changes.
      if (this.center && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleCoverflow());
        this.resizeObserver.observe(this.splide.Components.Elements.track);
      }
    }

    destroy() {
      this.stopFrameLoop();
      if (this.scheduledFrame) cancelAnimationFrame(this.scheduledFrame);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.splide) {
        this.splide.destroy(true);
        this.splide = null;
      }
    }

    // Defer a coverflow pass to the next frame after layout (double rAF), so
    // slide geometry is final before we measure. Coalesces repeated calls.
    scheduleCoverflow() {
      if (this.scheduled) return;
      this.scheduled = true;
      requestAnimationFrame(() => {
        this.scheduledFrame = requestAnimationFrame(() => {
          this.scheduled = false;
          this.applyCoverflow();
        });
      });
    }

    /* ---- Center-mode coverflow (event-driven) ---- */

    startFrameLoop() {
      if (this.frameActive) return;
      this.frameActive = true;
      this.runFrame();
    }

    stopFrameLoop() {
      this.frameActive = false;
      if (this.frame) cancelAnimationFrame(this.frame);
      this.applyCoverflow(); // settle on the final positions
    }

    runFrame() {
      if (!this.frameActive) return;
      this.applyCoverflow();
      this.frame = requestAnimationFrame(this.runFrame);
    }

    applyCoverflow() {
      if (!this.splide) return;
      const elements = this.splide.Components.Elements;
      const track = elements.track;
      const slides = elements.list.children;
      if (!track || !slides.length) return;

      // IMPORTANT: read the slide LI's geometry (Splide moves it, but we never
      // transform it) and write transforms to an INNER wrapper (which we never
      // read). Transforming the same element we measure would feed back into the
      // next frame's reading and make the scaling unstable.
      const trackRect = track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      const reads = [];
      let slideWidth = 0;
      for (let i = 0; i < slides.length; i += 1) {
        const el = slides[i];
        const rect = el.getBoundingClientRect();
        if (rect.width) {
          reads.push({ el, d: (rect.left + rect.width / 2 - center) / rect.width });
          if (rect.width > slideWidth) slideWidth = rect.width;
        }
      }
      if (!slideWidth) return;

      // Clamp the spread so a neighbour's OUTER edge never gets clipped past the
      // viewport. Outer edge from centre = slideWidth*(spread + 0.5*minScale);
      // it must stay within trackWidth/2. On a wide centre (mobile) this lowers
      // the spread automatically so the peek stays visible — no per-device value.
      const maxSpread = ((trackRect.width / 2 - 6) / slideWidth - 0.5 * this.minScale) * 100;
      const spread = Math.max(0, Math.min(this.spread, maxSpread));

      for (let i = 0; i < reads.length; i += 1) {
        const { el, d } = reads[i];
        const target = el.querySelector('[data-carousel-slide-inner]') || el;
        const absd = Math.abs(d);
        const scale = 1 - Math.min(absd, 1) * (1 - this.minScale);
        // Move each slide from its Splide slot (offset d*100%) to a resting
        // offset of d*spread%, i.e. stacked just behind the centre.
        const translate = -d * (100 - spread);
        // Only the centre + immediate neighbours are shown; fade out anything
        // further so we never see a 3rd/4th card behind.
        const opacity = 1 - Math.min(Math.max((absd - 1.3) / 0.5, 0), 1);

        target.style.transform = `translateX(${translate}%) scale(${scale})`;
        target.style.opacity = opacity.toFixed(3);
        el.style.zIndex = String(100 - Math.round(absd * 10));
        el.style.pointerEvents = opacity < 0.05 ? 'none' : '';
      }
    }

    options() {
      const data = this.dataset;
      const bool = (v, fallback) => (v === undefined ? fallback : v === 'true');
      const num = (v, fallback) => {
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? fallback : n;
      };

      const perPage = num(data.perPage, 1);
      const options = {
        type: this.loop ? 'loop' : 'slide',
        focus: data.center === 'true' ? 'center' : 0,
        perPage: perPage,
        perMove: 1,
        gap: data.gap || '1.5rem',
        arrows: false,
        pagination: false,
        autoplay: bool(data.autoplay, false),
        interval: num(data.interval, 5000),
        speed: num(data.speed, 600),
        // Smooth, symmetric ease-in-out (gentle start AND landing). Splide's default
        // easeOutQuart lurches at the start; since the coverflow rAF follows Splide's
        // eased motion, this curve drives the whole transition feel.
        easing: data.easing || 'cubic-bezier(0.65, 0, 0.35, 1)',
        pauseOnHover: bool(data.pauseOnHover, true),
        pauseOnFocus: true,
        drag: true,
        keyboard: 'focused',
        omitEnd: true
      };

      if (data.padding) options.padding = data.padding;
      // fixedWidth sets a fixed/"max" slide width; it overrides perPage for that
      // breakpoint, so a fixed card width naturally shows a partial next card.
      if (data.fixedWidth) options.fixedWidth = data.fixedWidth;

      const breakpoints = {};
      const tablet = {};
      if (data.perPageTablet) tablet.perPage = num(data.perPageTablet, perPage);
      if (data.gapTablet) tablet.gap = data.gapTablet;
      if (data.fixedWidthTablet) tablet.fixedWidth = data.fixedWidthTablet;
      if (Object.keys(tablet).length) breakpoints[990] = tablet;

      const mobile = {};
      if (data.perPageMobile) mobile.perPage = num(data.perPageMobile, perPage);
      if (data.paddingMobile) mobile.padding = data.paddingMobile;
      if (data.gapMobile) mobile.gap = data.gapMobile;
      if (data.fixedWidthMobile) mobile.fixedWidth = data.fixedWidthMobile;
      if (Object.keys(mobile).length) breakpoints[749] = mobile;

      if (Object.keys(breakpoints).length) options.breakpoints = breakpoints;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        options.autoplay = false;
      }

      return options;
    }

    bindArrows() {
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.splide && this.splide.go('<'));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.splide && this.splide.go('>'));
      }
    }

    refreshNav() {
      if (!this.splide) return;
      const index = this.splide.index;
      const length = this.splide.length;
      const perPage = this.splide.options.perPage || 1;

      // Hide the whole nav (progress bar + arrows) when there is nothing to
      // page through — i.e. every slide already fits at the current breakpoint.
      // Re-evaluated on resize, so it reappears if perPage drops on mobile.
      if (this.nav) this.nav.style.display = length > perPage ? '' : 'none';

      if (this.progressFill && length > 0) {
        const ratio = Math.min(1, (index + perPage) / length);
        this.progressFill.style.width = `${ratio * 100}%`;
      }

      // Loop carousels never bottom out; only toggle disabled in slide mode.
      if (!this.loop) {
        const end = this.splide.Components.Controller.getEnd();
        if (this.prevButton) this.prevButton.disabled = index <= 0;
        if (this.nextButton) this.nextButton.disabled = index >= end;
      }
    }
  }

  customElements.define('splide-carousel', SplideCarousel);

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const el = event.target.querySelector('splide-carousel');
      if (el) el.init();
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const el = event.target.querySelector('splide-carousel');
      if (el) el.destroy();
    });
    document.addEventListener('shopify:block:select', (event) => {
      const el = event.target.closest('splide-carousel');
      if (el && el.splide) {
        const slide = event.target.closest('.splide__slide');
        if (slide) {
          const index = parseInt(slide.dataset.slideIndex, 10);
          if (!Number.isNaN(index)) el.splide.go(index);
        }
      }
    });
  }
}
